import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Icon, Text, Tag } from '@nimbus-ds/components';
import { LocationIcon } from '@nimbus-ds/icons';
import {
  CurrencyType,
  FulfillmentOrderAssignedLocationDto,
  ProductsSortByEnum,
} from '@tiendanube/common';
import ModalAside from 'App/components/lab/ModalAside';
import { HeaderContent } from 'commons/components';
import useTranslationOrders from 'domains/Orders/useTranslationOrders/useTranslationOrders';
import { ProductSearcherContent } from './ProductSearcherContent';
import { PreSelectedProductInterface, ProductInterface } from './types';
import './ProductSearcher.scss';

export interface ProductSearcherFooterProps<Minimalistic extends boolean> {
  selectedProducts: ProductInterface<Minimalistic>[];
  selectedLimitReached: boolean;
  onClose: () => void;
  onSubmit: () => void;
}

interface ProductSearcherProps<Minimalistic extends boolean> {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (selectedProducts: ProductInterface<Minimalistic>[]) => void;
  preselectedProducts?: PreSelectedProductInterface<Minimalistic>[];
  sortBy?: ProductsSortByEnum | null;
  loadInitial?: boolean;
  onlyPublishedProducts?: boolean;
  location?: FulfillmentOrderAssignedLocationDto;
  requiresShipping?: boolean;
  minimalistic?: boolean;
  selectedProductsLimit?: number;
  currencyDisplay?: CurrencyType;
  showPrice: boolean;
  allowInfiniteStockSelection: boolean;
  footerHeight?: string;
  renderFooterCallback: (
    footerProps: ProductSearcherFooterProps<Minimalistic>,
  ) => JSX.Element;
}

export function ProductSearcher<Minimalistic extends boolean>({
  isOpen,
  onClose,
  onSubmit,
  preselectedProducts = [],
  sortBy = null,
  minimalistic = false,
  onlyPublishedProducts = false,
  selectedProductsLimit,
  location,
  requiresShipping,
  renderFooterCallback,
  currencyDisplay,
  showPrice,
  allowInfiniteStockSelection,
  footerHeight = '70px',
}: Readonly<ProductSearcherProps<Minimalistic>>) {
  const t = useTranslationOrders();

  // State to track selected products
  const [selectedProducts, setSelectedProducts] = useState<
    ProductInterface<Minimalistic>[]
  >([]);

  // When using the minimalistic mode, the preselected products are used as selected ones instead
  // to enable deselection. Not the prettiest, I know.
  const _preselectedProducts = useMemo(
    () => (minimalistic ? [] : preselectedProducts),
    [minimalistic, preselectedProducts],
  );

  // Handle product selection and deselection
  const handleProductSelect = useCallback(
    (products: ProductInterface<Minimalistic>[]) => {
      setSelectedProducts(products);
    },
    [],
  );

  const handleProductsSubmit = () => {
    onSubmit(selectedProducts);
    setSelectedProducts([]);
  };

  const handleClose = () => {
    onClose();
    setSelectedProducts([]);
  };

  useEffect(() => {
    if (minimalistic && isOpen) {
      setSelectedProducts(
        preselectedProducts as ProductInterface<Minimalistic>[],
      );
    }
  }, [minimalistic, setSelectedProducts, preselectedProducts, isOpen]);

  const selectedLimitReached = useMemo(
    () =>
      !!selectedProductsLimit &&
      selectedProducts.length + _preselectedProducts.length >=
        selectedProductsLimit,
    [selectedProductsLimit, selectedProducts, _preselectedProducts],
  );

  const footer = renderFooterCallback({
    onSubmit: handleProductsSubmit,
    onClose: handleClose,
    selectedProducts,
    selectedLimitReached,
  });

  return (
    <ModalAside
      hasVirtualizationContent
      isOpen={isOpen}
      onDidDismiss={handleClose}
      headerContent={
        <>
          <HeaderContent
            title={
              minimalistic
                ? t('productSearcher.titleSelectProducts')
                : t('productSearcher.titleAddProducts')
            }
          />
          {location && (
            <Box display="flex" marginTop="4">
              <Tag appearance="primary">
                <Icon source={<LocationIcon />} />
                {location.name}
              </Tag>
              {requiresShipping !== undefined && !requiresShipping && (
                <Box marginLeft="2">
                  <Tag appearance="primary">
                    {t('productSearcher.digitalProducts')}
                  </Tag>
                </Box>
              )}
            </Box>
          )}
          {minimalistic && (
            <Box display="flex" marginTop="4">
              <Text fontSize="base">
                {t('productSearcher.subtitleSelectedProductsLimitAlert', {
                  productSelectLimit: selectedProductsLimit,
                })}
              </Text>
            </Box>
          )}
        </>
      }
      footer={
        (!!selectedProducts.length || selectedLimitReached || minimalistic) &&
        footer
      }
      footerHeight={footerHeight}
    >
      <ProductSearcherContent
        isOpen={isOpen}
        selectedProducts={selectedProducts}
        setSelectedProducts={setSelectedProducts}
        preselectedProducts={_preselectedProducts}
        sortBy={sortBy}
        onlyPublishedProducts={onlyPublishedProducts}
        selectedProductsLimit={selectedProductsLimit}
        selectedLimitReached={selectedLimitReached}
        location={location}
        requiresShipping={requiresShipping}
        onProductSelect={handleProductSelect}
        currencyDisplay={currencyDisplay}
        minimalistic={minimalistic}
        showPrice={showPrice}
        allowInfiniteStockSelection={allowInfiniteStockSelection}
      />
    </ModalAside>
  );
}
